import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
  Stack,
  Button,
  Alert,
  Slide,
  Chip,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";
import type {
  ShipmentApi,
  ListShipmentsApiResponse,
  ShipmentAssignee,
} from "@services/shipments/types";
import { EventLogger } from "@shared/utils/eventLogger";

interface ShipmentSearchPanelProps {
  open: boolean;
  onClose: () => void;
  onSelectShipment: (shipmentId: string) => void;
}

function getProperty(
  properties: Array<{ name: string; value: string }>,
  name: string,
): string | null {
  return properties.find((p) => p.name === name)?.value ?? null;
}

function getProperties(
  properties: Array<{ name: string; value: string }>,
  name: string,
): string[] {
  return properties.filter((p) => p.name === name).map((p) => p.value);
}

export const ShipmentSearchPanel: React.FC<ShipmentSearchPanelProps> = ({
  open,
  onClose,
  onSelectShipment,
}) => {
  const { apiClient } = useOutlookPlugin();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shipments, setShipments] = useState<ShipmentApi[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const assigneesRef = useRef<Map<string, ShipmentAssignee>>(new Map());
  const [assigneesLoaded, setAssigneesLoaded] = useState(false);

  // Fetch assignees once on mount
  useEffect(() => {
    if (!open || assigneesLoaded) return;
    apiClient
      .post<{ assignees: ShipmentAssignee[] }>(
        "/shipments/assignee/list",
        {},
      )
      .then((res) => {
        const map = new Map<string, ShipmentAssignee>();
        for (const a of res.assignees) {
          if (a.id) map.set(a.id, a);
        }
        assigneesRef.current = map;
        setAssigneesLoaded(true);
      })
      .catch(() => {
        // non-critical, assignee names just won't resolve
      });
  }, [open, assigneesLoaded, apiClient]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const response = await apiClient.post<ListShipmentsApiResponse>(
        "/shipments/list",
        { search: searchQuery.trim(), sortBy: "last_update", offset: 0 },
      );
      EventLogger.getInstance().track({
        name: "shipment_timeline_search",
        query: searchQuery.trim(),
      });
      if (response.shipments.length === 1) {
        onSelectShipment(response.shipments[0].id);
        return;
      }
      setShipments(response.shipments);
    } catch {
      setError("Search failed");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, apiClient]);

  const handleClose = useCallback(() => {
    setSearchQuery("");
    setShipments([]);
    setHasSearched(false);
    setError(null);
    onClose();
  }, [onClose]);

  const resolveAssigneeName = (id: string): string | null => {
    const a = assigneesRef.current.get(id);
    if (!a) return null;
    return a.name || a.user?.name || a.user?.email || a.userEmail || null;
  };

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "background.paper",
          zIndex: 1300,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Black header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ bgcolor: "#000", color: "#fff", px: 1, py: 1, flexShrink: 0 }}
        >
          <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Search Shipment
          </Typography>
        </Stack>

        {/* Search input + button */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ px: 1, pt: 1, alignItems: "center", flexShrink: 0 }}
        >
          <TextField
            size="small"
            placeholder="Booking #, container, BL..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
            autoFocus
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={loading || !searchQuery.trim()}
            sx={{ minWidth: 0, px: 2 }}
          >
            Search
          </Button>
        </Stack>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* No results */}
        {hasSearched && !loading && !error && shipments.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No shipments found for this search.
            </Typography>
          </Box>
        )}

        {/* Results */}
        {shipments.length > 0 && !loading && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {shipments.length} shipment{shipments.length !== 1 ? "s" : ""}{" "}
              found
            </Typography>
            <Stack spacing={1}>
              {shipments.map((shipment) => {
                const pol = getProperty(
                  shipment.properties,
                  "port_of_loading",
                );
                const pod = getProperty(
                  shipment.properties,
                  "port_of_discharge",
                );
                const booking = getProperty(
                  shipment.properties,
                  "booking_number",
                );
                const bl = getProperty(
                  shipment.properties,
                  "master_bill_of_lading",
                );
                const containers = getProperties(
                  shipment.properties,
                  "container_number",
                );
                const containerDisplay =
                  containers.length > 0
                    ? containers.length > 1
                      ? `${containers[0]} (+${containers.length - 1})`
                      : containers[0]
                    : null;

                const assigneeNames = shipment.assignedToIds
                  .map(resolveAssigneeName)
                  .filter(Boolean) as string[];

                return (
                  <Box
                    key={shipment.id}
                    onClick={() => onSelectShipment(shipment.id)}
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "action.hover",
                        borderColor: "primary.main",
                      },
                      transition: "all 0.15s",
                    }}
                  >
                    <Stack spacing={0.25}>
                      {/* POL → POD */}
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            color: "text.secondary",
                          }}
                        >
                          {pol || "—"}
                        </Typography>
                        <ArrowForwardIcon
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            color: "text.secondary",
                          }}
                        >
                          {pod || "—"}
                        </Typography>
                      </Stack>

                      {/* Booking Number */}
                      {booking && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                        >
                          Booking #: {booking}
                        </Typography>
                      )}

                      {/* BL */}
                      {bl && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                        >
                          BL: {bl}
                        </Typography>
                      )}

                      {/* Container Numbers */}
                      {containerDisplay &&
                        (containers.length > 1 ? (
                          <Tooltip title={containers.join(", ")} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.75rem",
                                textDecoration: "underline",
                                cursor: "help",
                              }}
                            >
                              Container: {containerDisplay}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Container: {containerDisplay}
                          </Typography>
                        ))}

                      {/* Assignees */}
                      {assigneeNames.length > 0 && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                          flexWrap="wrap"
                        >
                          {assigneeNames.map((name) => (
                            <Chip
                              key={name}
                              label={name}
                              size="small"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
      </Box>
    </Slide>
  );
};
